'use client'

import dynamic from 'next/dynamic'
import { Grid2 as Grid, Box, Typography } from '@mui/material'

// Types
import type { APIStoreProductItem } from '@/types/api/items/store/store-product'

// Helpers
import { useTranslation } from '@/helpers/i18n/client'
import { useCardStore } from '@/helpers/store/card'

// Utils
import ToPersianNumber from '@/utils/to-persian-number'
import FormatNumber from '@/utils/format-number'
import Link from 'next/link'

// Components
const CurvedTriangleShapeAtom = dynamic(() => import('@/components/atoms/shapes/curved-triangle'))
const DiscountIconAtom = dynamic(() => import('@/components/atoms/icons/discount'))
const PrimaryButtonAtom = dynamic(() => import('@/components/atoms/buttons/text/primary'))

const ProductItemMolecule = (props: APIStoreProductItem) => {
	// Props
	const { name, alias, prices, media } = props

	// Varaibles
	const { t } = useTranslation()
	const { addToCart } = useCardStore()

	return (
		<Link href={`/product/${alias}`}>
			<Grid container p={{ xs: 1.25, md: 2 }} position="relative" bgcolor="#f5f5f5" borderRadius={3}>
				{media && media.length ? (
					<Grid size={12} position="relative">
						<Box
							width={1}
							height={175}
							borderRadius={2}
							overflow="hidden"
							sx={{
								'& picture': {
									display: 'inline-flex',
									width: 1,
									height: 1,
									'& img': {
										width: 1,
										height: 1,
										objectFit: 'cover',
										objectPosition: 'center'
									}
								}
							}}
						>
							<picture>
								<img src={`/api/download${media[0].url}`} alt={name ?? ''} />
							</picture>
						</Box>

						<Box
							sx={{
								'& svg': {
									position: 'absolute',
									top: '-50%',
									left: '-50%',
									transform: 'translate(-50%, -50%)'
								}
							}}
						>
							<Box position="absolute" left="50%" bottom={15} zIndex={2}>
								<CurvedTriangleShapeAtom color="#f5f5f5" />
							</Box>
						</Box>
					</Grid>
				) : null}

				<Grid size={12} mt={1.75}>
					<Typography variant="h3" fontSize={{ xs: 14, md: 16 }} fontWeight={500} lineHeight={1.5} letterSpacing={-1}>
						{t(name)}
					</Typography>
				</Grid>

				{prices?.length ? (
					<Grid size={12} mt={2.875}>
						<Grid container flexDirection={prices?.length ? 'row-reverse' : 'row'} justifyContent="space-between" alignItems="center" spacing={1}>
							{(prices?.length || prices[0].discounts?.length) && (
								<Grid size={6}>
									<Grid container flexDirection="column" alignItems="end">
										{prices[0].discounts?.length ? (
											<Grid size={12} display="flex" alignItems="center">
												{prices[0].value ? (
													<Typography
														component="span"
														display="block"
														fontSize={15}
														fontWeight={600}
														lineHeight={1.25}
														letterSpacing={-0.1}
														textOverflow="ellipsis"
														whiteSpace="nowrap"
														color="#d2d2d2"
														overflow="hidden"
														sx={{ textDecoration: 'line-through' }}
													>
														{ToPersianNumber(FormatNumber(prices[0].value))}
													</Typography>
												) : null}

												{prices[0].discounts?.length && prices[0].discounts[0].value ? (
													<Typography
														component="span"
														display="inline-flex"
														alignItems="center"
														ml={0.5}
														fontSize={15}
														fontWeight={600}
														lineHeight={1.25}
														letterSpacing={-0.1}
														color="#e91c41"
													>
														{ToPersianNumber(`${prices[0].discounts[0].percent}%`)}

														<DiscountIconAtom
															sx={{
																ml: 0.25,
																fontSize: 15
															}}
														/>
													</Typography>
												) : null}
											</Grid>
										) : null}

										{prices[0].value ? (
											<Grid size={12} display="flex" alignItems="center">
												<Typography
													component="span"
													display="block"
													fontSize={18}
													fontWeight={700}
													lineHeight={1.5}
													letterSpacing={-0.02}
													textOverflow="ellipsis"
													whiteSpace="nowrap"
													overflow="hidden"
												>
													{ToPersianNumber(FormatNumber(prices[0].value))}
												</Typography>

												<Typography component="span" ml={0.5} fontSize={12} fontWeight={400} lineHeight={1.5} letterSpacing={-0.02}>
													{t('common:title.toman')}
												</Typography>
											</Grid>
										) : null}
									</Grid>
								</Grid>
							)}

							<Grid>
								<PrimaryButtonAtom
									title="form:button.addToBasket"
									onClick={() =>
										addToCart({
											product: {
												category: {
													name: t('common:sample.categories.gaz'),
													alias: 'gaz',
													sub_index_number: 0,
													index_number: 0,
													link_value: ''
												},
												name: t('common:sample.product.title'),
												alias: 'test',
												description: t('common:sample.product.description'),
												sold_total: 0,
												prices: [
													{
														currency: {
															name: 'USD',
															alias: 'usd'
														},
														value: 900000,
														number_items: 10,
														number_per_package: 1,
														number_sold: 0,
														discounts: [
															{
																percent: 21,
																value: 25000,
																expire_date: Date.now().toString()
															}
														]
													}
												],
												media: [
													{
														url: '/images/samples/product29.png',
														thumbnails: [{ url: '/images/samples/thumbnail-mobile-1.png', size: 100 }]
													}
												],
												code: ''
											},
											price: 1000000,
											totalPrice: 1000000,
											numberOfItem: 1,
											maximum: 10,
											discountPrice: 100000
										})
									}
								/>
							</Grid>
						</Grid>
					</Grid>
				) : null}
			</Grid>
		</Link>
	)
}

export default ProductItemMolecule
